'use client';

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { ChevronRight, FolderKanban, FileText, Package, DollarSign, Lightbulb, Loader2, MoreVertical, Pencil, Trash2, Download, Link2, Check, Sparkles } from 'lucide-react';
import { useProjects, useProjectSubfolders, useMcpRfqLink, useMcpRfqUnlink } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { useRfqActions } from '@/lib/hooks/use-rfq-actions';
import { cn } from '@/lib/utils/cn';
import type { ProjectSummary, ProjectExploreData, McpSummary, ProductWishSummary } from '@/lib/api/types';
import { testIds } from '@/lib/test-utils/selectors';

type ChildItem = ProjectExploreData['rfqs'][number] | ProjectExploreData['pos'][number];

export function ProjectsExplorer() {
  const projectsStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const { data, isLoading, error } = useProjects(projectsStrategy!, { limit: 50 });
  const [stateFilter, setStateFilter] = useState<string>('all');

  // Collect unique states from all loaded project children
  // (derived from data, not from a separate API call)
  const allStates = useMemo(() => {
    if (!data?.projects) return [];
    // States will populate as projects expand; static list covers common Odoo states
    return ['draft', 'sent', 'purchase', 'done', 'cancel'];
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Failed to load projects. Check connection.
      </div>
    );
  }

  if (!data?.projects.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No projects found.
      </div>
    );
  }

  return (
    <div data-testid={testIds.explorer.projectList} className="py-1">
      {/* Status filter bar */}
      <div className="flex flex-wrap gap-1 px-2 pb-2">
        <FilterChip label="All" active={stateFilter === 'all'} onClick={() => setStateFilter('all')} />
        {allStates.map((s) => (
          <FilterChip key={s} label={s} active={stateFilter === s} onClick={() => setStateFilter(s)} />
        ))}
      </div>

      {data.projects.map((project) => (
        <ProjectTreeItem key={project.semantic_id} project={project} stateFilter={stateFilter} />
      ))}
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-0.5 text-xs rounded-full border transition-colors capitalize',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-transparent text-muted-foreground border-border hover:border-foreground/30'
      )}
    >
      {label}
    </button>
  );
}

function ProjectTreeItem({ project, stateFilter }: { project: ProjectSummary; stateFilter: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = project.rfq_count > 0 || project.po_count > 0;
  const openTab = useTabStore((state) => state.openTab);
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // V2: data gateway strategy call — lightweight children for sub-folders
  const exploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const { data: response, isLoading } = useProjectSubfolders(
    exploreStrategy!,
    expanded ? project.semantic_id : null
  );

  const exploreData = response?.data as ProjectExploreData | undefined;

  // Apply state filter to RFQs and POs (MCPs have no state — always shown)
  const filteredRfqs = useMemo(() => {
    if (!exploreData?.rfqs) return [];
    if (stateFilter === 'all') return exploreData.rfqs;
    return exploreData.rfqs.filter((r) => r.state === stateFilter);
  }, [exploreData?.rfqs, stateFilter]);

  const filteredPos = useMemo(() => {
    if (!exploreData?.pos) return [];
    if (stateFilter === 'all') return exploreData.pos;
    return exploreData.pos.filter((p) => p.state === stateFilter);
  }, [exploreData?.pos, stateFilter]);

  const mcps = exploreData?.mcps ?? [];
  const wishes = exploreData?.product_wishes ?? [];

  // DEBUG: remove after verifying
  if (expanded && exploreData) {
    console.log('[ProjectExplorer] exploreData keys:', Object.keys(exploreData), 'wishes:', wishes.length, 'raw product_wishes:', exploreData.product_wishes);
  }

  const handleOpenProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    openTab({
      id: `project-${project.semantic_id}`,
      type: 'project',
      title: project.name,
      entityId: project.semantic_id,
    });
  };

  const handleToggleExpand = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  return (
    <div>
      {/* Project Row */}
      <div
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1 text-sm',
          'hover:bg-accent/50 transition-colors',
          !hasChildren && 'pl-6'
        )}
      >
        {hasChildren && (
          <button onClick={handleToggleExpand}>
            <ChevronRight
              className={cn(
                'w-4 h-4 shrink-0 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </button>
        )}
        <button
          data-testid={testIds.explorer.projectItem(project.semantic_id)}
          onClick={handleOpenProject}
          className="flex items-center gap-1 flex-1 min-w-0 text-left"
        >
          <FolderKanban className="w-4 h-4 shrink-0 text-blue-400" />
          <span className="truncate flex-1">{project.name}</span>
        </button>
        {(project.rfq_count > 0 || project.po_count > 0) && (
          <span className="text-xs text-muted-foreground shrink-0">
            {project.rfq_count > 0 && `${project.rfq_count} RFQ`}
            {project.rfq_count > 0 && project.po_count > 0 && ', '}
            {project.po_count > 0 && `${project.po_count} PO`}
          </span>
        )}
      </div>

      {/* Expanded Children — sub-folder sections */}
      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-1 text-sm text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </div>
          ) : (
            <>
              {filteredRfqs.length > 0 && (
                <ChildSection label="RFQs" icon={FileText} iconColor="text-yellow-400" count={filteredRfqs.length}>
                  {filteredRfqs.map((rfq) => (
                    <ChildRow
                      key={rfq.semantic_id}
                      item={rfq}
                      type="rfq"
                      icon={FileText}
                      iconColor="text-yellow-400"
                      testId={testIds.explorer.rfqItem(rfq.semantic_id || '')}
                      projectSid={project.semantic_id}
                      selectNode={selectNode}
                      mcps={mcps}
                    />
                  ))}
                </ChildSection>
              )}

              {filteredPos.length > 0 && (
                <ChildSection label="Purchase Orders" icon={Package} iconColor="text-green-400" count={filteredPos.length}>
                  {filteredPos.map((po) => (
                    <ChildRow
                      key={po.semantic_id}
                      item={po}
                      type="po"
                      icon={Package}
                      iconColor="text-green-400"
                      testId={testIds.explorer.orderItem(po.semantic_id || '')}
                      projectSid={project.semantic_id}
                      selectNode={selectNode}
                    />
                  ))}
                </ChildSection>
              )}

              {mcps.length > 0 && (
                <ChildSection label="Cost Plans" icon={DollarSign} iconColor="text-amber-400" count={mcps.length}>
                  {mcps.map((mcp) => (
                    <McpRow key={mcp.semantic_id} mcp={mcp} projectSid={project.semantic_id} selectNode={selectNode} />
                  ))}
                </ChildSection>
              )}

              {wishes.length > 0 && (
                <ChildSection label="Product Wishes" icon={Lightbulb} iconColor="text-orange-400" count={wishes.length}>
                  {wishes.map((wish) => (
                    <WishRow key={wish.semantic_id} wish={wish} projectSid={project.semantic_id} selectNode={selectNode} />
                  ))}
                </ChildSection>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Collapsible section header with count badge. */
function ChildSection({
  label,
  icon: Icon,
  iconColor,
  count,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight className={cn('w-3 h-3 shrink-0 transition-transform', open && 'rotate-90')} />
        <Icon className={cn('w-3 h-3 shrink-0', iconColor)} />
        <span className="font-medium">{label}</span>
        <span className="ml-auto text-[10px] bg-muted rounded-full px-1.5">{count}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

/** Single RFQ or PO child row — click to view, kebab for Edit / Download. */
function ChildRow({
  item,
  type,
  icon: Icon,
  iconColor,
  testId,
  projectSid,
  selectNode,
  mcps,
}: {
  item: ChildItem;
  type: 'rfq' | 'po';
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  testId: string;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; status?: string; properties: Record<string, unknown> }, projectId: string) => void;
  mcps?: McpSummary[];
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const { editRfq, downloadRfq, downloadRfqTypst, cancelRfq } = useRfqActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkSubmenuOpen, setLinkSubmenuOpen] = useState(false);
  const rfqLinkStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rfq_link'));
  const rfqUnlinkStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rfq_unlink'));
  const rfqLink = useMcpRfqLink(rfqLinkStrategy!);
  const rfqUnlink = useMcpRfqUnlink(rfqUnlinkStrategy!);

  // Only RFQs carry linked_plan_sid/name (see ProjectExploreData['rfqs']) —
  // POs don't have the concept, so this is null for type==='po'.
  const linkedPlanSid = type === 'rfq' ? (item as ProjectExploreData['rfqs'][number]).linked_plan_sid : null;
  const linkedPlanName = type === 'rfq' ? (item as ProjectExploreData['rfqs'][number]).linked_plan_name : null;

  const handleLinkToPlan = (plan: McpSummary) => {
    setMenuOpen(false);
    setLinkSubmenuOpen(false);
    rfqLink.mutate(
      { project_sid: projectSid, plan_sid: plan.semantic_id, rfq_sid: item.semantic_id || '' },
      {
        onSuccess: () => toast.success(`Linked to ${plan.name}`),
        onError: (err) => toast.error('Link failed', { description: err.message }),
      },
    );
  };

  const handleUnlink = () => {
    setMenuOpen(false);
    setLinkSubmenuOpen(false);
    if (!linkedPlanSid) return;
    rfqUnlink.mutate(
      { project_sid: projectSid, plan_sid: linkedPlanSid, rfq_sid: item.semantic_id || '' },
      {
        onSuccess: () => toast.success('Unlinked'),
        onError: (err) => toast.error('Unlink failed', { description: err.message }),
      },
    );
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        'group relative w-full flex items-center gap-1 px-2 py-1 text-sm hover:bg-accent/50 pl-6',
        selectedNode?.id === item.semantic_id && 'bg-accent'
      )}
    >
      <button
        type="button"
        onClick={() => selectNode({
          id: item.semantic_id || '',
          label: item.name || type.toUpperCase(),
          type,
          status: item.state || undefined,
          properties: item as unknown as Record<string, unknown>,
        }, projectSid)}
        className="flex items-center gap-1 flex-1 min-w-0 text-left"
      >
        <Icon className={cn('w-4 h-4 shrink-0', iconColor)} />
        <span className="truncate flex-1">{item.name}</span>
        {linkedPlanName && (
          <span
            title={`Linked to ${linkedPlanName}`}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs shrink-0 max-w-20"
          >
            <Link2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{linkedPlanName}</span>
          </span>
        )}
        <span className="text-xs text-muted-foreground">{item.state}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="RFQ actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-7 z-50 w-48 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void editRfq(item.semantic_id || '');
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadRfq(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadRfqTypst(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Download className="w-3.5 h-3.5" /> Download (Typst)
            </button>
            {type === 'rfq' && mcps && mcps.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkSubmenuOpen((v) => !v);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left truncate"
                >
                  <Link2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{linkedPlanName ? `Linked to ${linkedPlanName}` : 'Link to Cost Plan…'}</span>
                </button>
                {linkSubmenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-full top-0 mr-1 w-44 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
                  >
                    {mcps.map((plan) => {
                      const isCurrent = plan.semantic_id === linkedPlanSid;
                      return (
                        <button
                          key={plan.semantic_id}
                          type="button"
                          role="menuitem"
                          disabled={rfqLink.isPending || rfqUnlink.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCurrent) handleUnlink();
                            else handleLinkToPlan(plan);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left truncate disabled:opacity-50',
                            isCurrent && 'text-emerald-400',
                          )}
                        >
                          {isCurrent ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 shrink-0" />}
                          <span className="truncate">{plan.name}</span>
                        </button>
                      );
                    })}
                    {linkedPlanSid && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={rfqUnlink.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnlink();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left border-t border-border mt-1 pt-1.5 disabled:opacity-50"
                      >
                        Unlink
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void cancelRfq(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Single MCP (CostPlan) child row — click to view, kebab for Edit / Delete. */
function McpRow({
  mcp,
  projectSid,
  selectNode,
}: {
  mcp: McpSummary;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; properties: Record<string, unknown> }, projectId: string) => void;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const { editMcp, deleteMcp, aiProfileMcp, downloadMcp, downloadMcpSchedule, downloadMcpScheduleTypst } = useMcpActions();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={cn(
        'group relative w-full flex items-center gap-1 px-2 py-1 text-sm hover:bg-accent/50 pl-6',
        selectedNode?.id === mcp.semantic_id && 'bg-accent',
      )}
    >
      <button
        type="button"
        onClick={() => selectNode({
          id: mcp.semantic_id,
          label: mcp.name || 'Cost Plan',
          type: 'mcp',
          properties: mcp as unknown as Record<string, unknown>,
        }, projectSid)}
        className="flex items-center gap-1 flex-1 min-w-0 text-left"
      >
        <DollarSign className="w-4 h-4 shrink-0 text-amber-400" />
        <span className="truncate flex-1">{mcp.name}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Cost plan actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-7 z-50 w-36 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void editMcp(mcp.semantic_id);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void aiProfileMcp(mcp.semantic_id);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Sparkles className="w-3.5 h-3.5" /> Klyde AI Profile
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <DollarSign className="w-3.5 h-3.5" /> Cost Plan
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpSchedule(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Project Schedule
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpScheduleTypst(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Schedule (Typst)
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void deleteMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Single buyer product wish row. */
function WishRow({
  wish,
  projectSid,
  selectNode,
}: {
  wish: ProductWishSummary;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; properties: Record<string, unknown> }, projectId: string) => void;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);

  return (
    <button
      onClick={() => selectNode({
        id: wish.semantic_id,
        label: wish.name || 'Product Wish',
        type: 'product_wish',
        properties: wish as unknown as Record<string, unknown>,
      }, projectSid)}
      className={cn(
        'w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-6',
        selectedNode?.id === wish.semantic_id && 'bg-accent'
      )}
    >
      <Lightbulb className="w-4 h-4 shrink-0 text-orange-400" />
      <span className="truncate flex-1">{wish.name}</span>
      {wish.category && (
        <span className="text-xs text-muted-foreground capitalize">{wish.category}</span>
      )}
    </button>
  );
}

